import { routerNavigatedAction } from '@ngrx/router-store';
import { on } from "@ngrx/store";

import { cloneDeep, pull } from "lodash";
import { DEFAULT_ROOM_OPTIONS, FIELD_CONFIG } from "src/app/global.constants";
import { safeParseJSON } from "src/app/js";
import { generateUUID } from "src/app/utilities/state.util";

import { DiscardOpportunityDialogActions } from "../job-create/discard-job-component/discard-job.actions";
import { JobCreateInventoryActions } from "../job-create/job-create-inventory/job-create-inventory.actions";
import { NavigatorOutboxActions } from "../job-navigator/navigator-outbox/navigator-outbox.actions";
import { JobToolSubscriptionActions } from "../job-state/job-tool-subscription.actions";
import { JobToolActions } from "../job-tool.actions";


import { InventoryActions } from "./job-inventory.actions";
import { InventoryItem, InventoryState, JobInventory, jobInventoryInitialState } from "./job-inventory.state";

export const jobInventoryReducers = [
    on(
      JobToolActions.jobLoaded,
      JobToolActions.jobDataReset,
      JobToolSubscriptionActions.remoteJobUpdated,
      NavigatorOutboxActions.jobInventoryUpdatedLocally,
      (state: InventoryState, { job }): InventoryState => {

        const inventoryField = job.fields?.find((f) => f.name === FIELD_CONFIG.jobs.inventory.name);

        const jobInventory: JobInventory = safeParseJSON(inventoryField?.values?.[0]?.value, {});

        // Handle legacy inventory
        if (jobInventory.rooms && Array.isArray(jobInventory.rooms)) {

          let items: InventoryItem[] = [];

          for (const room of jobInventory.rooms) {

            if (!room || typeof room !== 'object') continue;

            if (!room.id) {
              room.id = generateUUID();
            }

            if (room.inventory && Array.isArray(room.inventory)) {
              items = [ ...items, ...room.inventory ];
            }
          }

          for (const item of items) {

            if (!item || typeof item !== 'object') continue;

            if (!item.id) {
              item.id = generateUUID();
            }
          }
        }

        const inventoryEditor = {
          ...jobInventoryInitialState.inventoryEditor,
          ...jobInventory,
        };

        return {
          ...state,
          inventoryEditor,
        };
      },
    ),
    on(JobToolActions.saveChangesSuccess, (state: InventoryState, { updatedJob }): InventoryState => {

      const { fields } = updatedJob;

      // Use helper to parse inventory as well
      const inventoryField = fields?.find((f) => f.name === FIELD_CONFIG.jobs.inventory.name);
      
      const jobInventory: JobInventory = safeParseJSON(inventoryField?.values[0]?.value, {});
      
      // Fallback or merge with other logic if needed, but primary source is field
      const inventoryEditor = {
        ...jobInventoryInitialState.inventoryEditor,
        ...jobInventory,
      };

      return {
        ...state,
        inventoryEditor,
      };
    }),
    on(InventoryActions.addRoomButtonClicked, (state: InventoryState): InventoryState => {

      return {
        ...state,
        inventoryEditor: {
          ...state.inventoryEditor,
          touched: false,
          customRoomName: '',
          addRoomDialogVisible: true,
        }
      };
    }),
    on(InventoryActions.exitAddRoomDialogButtonClicked, (state: InventoryState): InventoryState => {

      return {
        ...state,
        inventoryEditor: {
          ...state.inventoryEditor,
          customRoomName: '',
          addRoomDialogVisible: false,
        }
      };
    }),
    on(InventoryActions.roomAdded, (state: InventoryState, { room }): InventoryState => {

      return {
        ...state,
        inventoryEditor: {
          ...state.inventoryEditor,
          rooms: [
            ...state.inventoryEditor.rooms,
            room,
          ],
          customRoomName: '',
          addRoomDialogVisible: false,
        }
      };
    }),
    on(InventoryActions.customRoomNameInputChanged, (state: InventoryState, { name }): InventoryState => {
      return {
        ...state,
        inventoryEditor: {
          ...state.inventoryEditor,
          touched: true,
          customRoomName: name,
        }
      };
    }),
    on(
      InventoryActions.editRoomNameInPlaceConfirmButtonClicked,
      (state: InventoryState, { name, roomId }): InventoryState => {

        const rooms = [ ...state.inventoryEditor.rooms ];

        const roomIndex = rooms.findIndex((r) => r.id === roomId);

        if (roomIndex === -1) {
          return state;
        }

        const room = {
          ...rooms[roomIndex],
          name,
        }

        rooms.splice(roomIndex, 1, room);

        return {
          ...state,
          inventoryEditor: {
            ...state.inventoryEditor,
            rooms,
          }
        };
      }
    ),
    on(InventoryActions.toggleCollapseRoomButtonClicked, (state: InventoryState, { roomId }): InventoryState => {

        const rooms = [ ...state.inventoryEditor.rooms ];

        const roomIndex = rooms.findIndex((r) => r.id === roomId);

        if (roomIndex === -1) {
          return state;
        }

        const originalRoom = rooms[roomIndex];

        const room = {
          ...originalRoom,
          isCollapsed: !originalRoom.isCollapsed,
        }

        rooms.splice(roomIndex, 1, room);

        return {
          ...state,
          inventoryEditor: {
            ...state.inventoryEditor,
            rooms,
          }
        };
    }),
    on(
      InventoryActions.removeRoomButtonClicked,
      JobCreateInventoryActions.removeRoomButtonClicked,
      (state: InventoryState, { roomId }): InventoryState => {
          return {
            ...state,
            inventoryEditor: {
              ...state.inventoryEditor,
              rooms: state.inventoryEditor.rooms.filter((r) => r.id !== roomId),
            }
          };
        },
    ),
    on(
      InventoryActions.itemAutocompleteInputChanged,
      JobCreateInventoryActions.itemAutocompleteInputChanged,
      (state: InventoryState, { value, roomId }): InventoryState => {
          return {
            ...state,
            inventoryEditor: {
              ...state.inventoryEditor,
              inventorySearch: {
                ...state.inventoryEditor.inventorySearch,
                [roomId]: value,
              },
            },
          };
        },
    ),
    on(
      InventoryActions.itemAutocompleteSearchFired,
      JobCreateInventoryActions.itemAutocompleteSearchFired,
      (state: InventoryState): InventoryState => {
          return {
            ...state,
            inventoryEditor: {
              ...state.inventoryEditor,
              inventorySearchLoading: true,
              inventorySearchResults: [],
            },
          };
        },
    ),
    on(InventoryActions.itemSearchSuccess, (state: InventoryState, { items }): InventoryState => {
      return {
        ...state,
        inventoryEditor: {
          ...state.inventoryEditor,
          inventorySearchResults: items,
          inventorySearchLoading: false,
        },
      };
    }),
    on(
      InventoryActions.itemAdded,
      (state: InventoryState, { item, roomId }): InventoryState => {

        const rooms = cloneDeep(state.inventoryEditor.rooms);

        const room = rooms.find((r) => r.id === roomId);

        if (!room) {
          return state;
        }

        if (!Array.isArray(room.inventory)) {
          room.inventory = [];
        }

        room.inventory.push(item);

        return {
          ...state,
          inventoryEditor: {
            ...state.inventoryEditor,
            rooms,
            inventorySearch: {
              ...state.inventoryEditor.inventorySearch,
              [roomId]: '',
            },
            inventorySearchLoading: false,
            inventorySearchResults: [],
          }
        };
      },
    ),
		on(
		  InventoryActions.removeItemButtonClicked,
      JobCreateInventoryActions.removeItemButtonClicked,
		  (state: InventoryState, { itemId, roomId }): InventoryState => {
		
		      const rooms = cloneDeep(state.inventoryEditor.rooms);
		
		      const room = rooms.find((r) => r.id === roomId);

		      if (!room || !Array.isArray(room.inventory)) {
		        return state;
		      }

		      room.inventory = room.inventory.filter((i) => i.id !== itemId);
		
		      return {
		        ...state,
		        inventoryEditor: {
		          ...state.inventoryEditor,
		          rooms,
		        }
		      };
				},
		),
		on(
		  InventoryActions.itemQuantityInputChanged,
      JobCreateInventoryActions.itemQuantityInputChanged,
		  (state: InventoryState, { itemId, roomId, quantity }): InventoryState => {
		
		      const rooms = cloneDeep(state.inventoryEditor.rooms);
		
		      const room = rooms.find((r) => r.id === roomId);

		      const item = room?.inventory?.find((i) => i.id === itemId);

		      if (!item) {
		        return state;
		      }

		      item.quantity = quantity;
		
		      return {
		        ...state,
		        inventoryEditor: {
		          ...state.inventoryEditor,
		          rooms,
		        }
		      };
				},
		),
		on(InventoryActions.editItemButtonClicked, (state: InventoryState, { itemId, roomId }): InventoryState => {

      const room = state.inventoryEditor.rooms.find((r) => r.id === roomId);

      const item = room?.inventory?.find((i) => i.id === itemId);

      if (!item) {
        return state;
      }

      return {
        ...state,
        editInventoryItemDialog: {
            selectedItem: {
                itemId,
                roomId,
            },
            name: item.name,
            quantity: item.quantity,
            weight: item.weight,
            volume: item.volume,
        },
      };
		}),
		on(
		  InventoryActions.exitEditItemButtonClicked,
      InventoryActions.cancelEditItemButtonClicked,
		  (state: InventoryState): InventoryState => {
		      return {
		        ...state,
		        editInventoryItemDialog: {
              ...jobInventoryInitialState.editInventoryItemDialog,
		        },
		      };
				},
		),
    on(InventoryActions.editItemDialogNameInputChanged, (state: InventoryState, { name }): InventoryState => {
      return {
        ...state,
        editInventoryItemDialog: {
          ...state.editInventoryItemDialog,
          name,
        }
      };
    }),
    on(InventoryActions.editItemDialogQuantityInputChanged, (state: InventoryState, { quantity }): InventoryState => {
      return {
        ...state,
        editInventoryItemDialog: {
          ...state.editInventoryItemDialog,
          quantity,
        }
      };
    }),
    on(InventoryActions.editItemDialogWeightInputChanged, (state: InventoryState, { weight }): InventoryState => {
      return {
        ...state,
        editInventoryItemDialog: {
          ...state.editInventoryItemDialog,
          weight,
        }
      };
    }),
    on(InventoryActions.editItemDialogVolumeInputChanged, (state: InventoryState, { volume }): InventoryState => {
      return {
        ...state,
        editInventoryItemDialog: {
          ...state.editInventoryItemDialog,
          volume,
        }
      };
    }),
    on(InventoryActions.itemEdited, (state: InventoryState, { roomId, item }): InventoryState => {

      const rooms = cloneDeep(state.inventoryEditor.rooms);

      const room = rooms.find((r) => r.id === roomId);

      const itemIndex = room?.inventory?.findIndex((i) => i.id === item.id) ?? -1;

      if (!room || itemIndex === -1) {
        return state;
      }

      room.inventory.splice(itemIndex, 1, item);

      return {
        ...state,
        inventoryEditor: {
          ...state.inventoryEditor,
          rooms,
        },
        editInventoryItemDialog: {
          ...jobInventoryInitialState.editInventoryItemDialog,
        },
      };
    }),
    on(JobCreateInventoryActions.roomAutocompleteInputChanged, (state: InventoryState, { value }): InventoryState => {
      return {
        ...state,
        inventoryEditor: {
          ...state.inventoryEditor,
          roomSearch: value,
        },
      };
    }),
    on(JobCreateInventoryActions.roomAutocompleteSearchFired, (state: InventoryState, { query }): InventoryState => {

      const roomSearchResults = DEFAULT_ROOM_OPTIONS
        .filter((o) => o.toLowerCase().includes(query.toLowerCase()))

      if (!roomSearchResults.length) {
        roomSearchResults.push(query);
      }

      return {
        ...state,
        inventoryEditor: {
          ...state.inventoryEditor,
          roomSearchResults,
        }
      };
    }),
    on(JobCreateInventoryActions.roomSelected, (state: InventoryState, { roomId, name }): InventoryState => {

      const inventoryEditor = cloneDeep(state.inventoryEditor);

      const room = inventoryEditor.rooms.find((r) => r.id === roomId);

      if (!room) {
        return state;
      }

      room.name = name;

      return {
        ...state,
        inventoryEditor,
      };
    }),
    on(InventoryActions.initialYemboOptionsLoaded, (state: InventoryState, { yemboOptions }): InventoryState => {
      return {
        ...state,
        yemboOptions,
      };
    }),
    on(JobCreateInventoryActions.yemboOptionCheckboxClicked, (state: InventoryState, { option }): InventoryState => {

      const yemboOptions = [ ...state.yemboOptions ];

      if (yemboOptions.includes(option)) {
        pull(yemboOptions, option);
      } else {
        yemboOptions.push(option);
      }

      return {
        ...state,
        yemboOptions,
      };
    }),
    on(DiscardOpportunityDialogActions.discardUnsavedChangesButtonClicked, (): InventoryState => jobInventoryInitialState),
    on(routerNavigatedAction, (state: InventoryState, { payload: { event: { urlAfterRedirects } } }): InventoryState => {
      if (urlAfterRedirects.startsWith('/jobs/new')) {
        return jobInventoryInitialState;
      }
      return state;
    }),
] as const;
